import { IncrementalQuantityField } from '@/components/forms/incremental-quantity-field';
import type { ButtonProps } from '@/components/ui/button';
import { Button } from '@/components/ui/button'; // Importing the hook to us
import { useToast } from '@/components/ui/use-toast';
import { useAnalytics } from '@/features/analytics/hooks/use-analytics';
import { DraftMachineContext } from '@/features/sales-draft/machines/draft-machine';
import type { SelectedLineItemProduct } from '@/features/sales-draft/types/normal-line-machine-types';
import { useLocale } from '@/lib/hooks/use-locale';
import type { ProductVariantDTO } from '@/server/dto/product-variant-dto';
import { useMemo, useState } from 'react';

interface Props extends Pick<ButtonProps, 'variant' | 'color'> {
    product: ProductVariantDTO;
}

const InventoryDetailsQuickAdd = ({ product: productVariant, variant = 'primary', color }: Props) => {
    const product = useMemo(() => {
        return {
            ...productVariant,
            article_group_id: productVariant.article_group_id ?? '',
            sales_units_per_package_unit: productVariant.sales_units_per_package_unit || 1,
            quantity_unit_sales: productVariant.quantity_unit_sales ?? '',
            package_unit: productVariant.package_unit ?? '',
        } satisfies SelectedLineItemProduct;
    }, [productVariant]);

    const [quantity, setQuantity] = useState(product.sales_units_per_package_unit); // Default quantity
    const { t } = useLocale('product-details'); // Hook to access the translation function
    const { toast } = useToast();

    const sendEvent = useAnalytics();

    const draftMachineRef = DraftMachineContext.useActorRef();

    const draftOrderId = DraftMachineContext.useSelector((s) => s.context.draftOrderId);

    return (
        <div className="flex items-center space-x-3">
            <IncrementalQuantityField
                className="w-[200px]"
                value={quantity}
                onValueChange={setQuantity}
                partialPackingType={product.partial_packing_type ?? undefined}
                quantityUnit={product.quantity_unit_sales}
                salesUnitsPerPackageUnit={product.sales_units_per_package_unit}
            />
            <Button
                variant={variant}
                color={color}
                onClick={() => {
                    draftMachineRef.send({
                        type: 'lineItems.add.normal',
                        payload: {
                            product,
                            quantity,
                        },
                    });

                    sendEvent('item_added_from_product_detail_page', {
                        draft_id: draftOrderId,
                        product_variant_id: product.product_variant_id,
                        article_group_id: product.article_group_id,
                        quantity,
                    });

                    toast({
                        icon: 'success',
                        variant: 'default',
                        title: t('toast__product_added_title', { ns: 'catalog' }),
                        description: t('toast__product_added_body', { ns: 'catalog' }).replace(
                            '{{quantity}}',
                            quantity.toString(),
                        ),
                    });
                }}
            >
                {t('quick_add')}
            </Button>
        </div>
    );
};

export default InventoryDetailsQuickAdd;
